import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import publicTenantService from '../services/publicTenantService';
import { setResolvedTenant } from '../utils/tenantResolutionStore';

/**
 * Dev/local hostnames for which we skip the hostname lookup entirely.
 * These always fall through to slug-based routing.
 */
const DEV_HOSTNAME_PATTERN = /\.local$/;
const DEV_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]']);
const DEV_LOOPBACK_PATTERN = /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;

const isDevHostname = (hostname) => {
  if (typeof window !== 'undefined' && window.__BYPASS_HOSTNAME_DEV_GUARD__) {
    return false;
  }
  return DEV_HOSTNAMES.has(hostname) ||
    DEV_HOSTNAME_PATTERN.test(hostname) ||
    DEV_LOOPBACK_PATTERN.test(hostname);
};

/**
 * Shape of the context value:
 * {
 *   status: 'loading' | 'matched' | 'unmatched',
 *   tenant: { slug, name, themeConfig, logoUrl } | null
 * }
 */
const HostnameTenantContext = createContext({
  status: 'loading',
  tenant: null
});

/**
 * Provider that resolves the current hostname to a tenant at app bootstrap.
 * Skips the fetch entirely for dev hostnames (localhost, 127.x.x.x, ::1, *.local).
 * Treats any 4xx response or network error defensively as 'unmatched'.
 * Uses an AbortController whose signal is forwarded to axios to handle both
 * React 18 StrictMode double-effect fires and genuine unmounts.
 */
export const HostnameTenantProvider = ({ children }) => {
  /**
   * Lazy initializer: dev hostnames resolve immediately to 'unmatched' without
   * a 'loading' flicker.  Non-dev hostnames start in 'loading'.
   */
  const [state, setState] = useState(() =>
    isDevHostname(window.location.hostname)
      ? { status: 'unmatched', tenant: null }
      : { status: 'loading', tenant: null }
  );

  useEffect(() => {
    const controller = new AbortController();
    const hostname = window.location.hostname;

    if (isDevHostname(hostname)) {
      setResolvedTenant(null);
      setState({ status: 'unmatched', tenant: null });
      return () => controller.abort();
    }

    publicTenantService.getTenantByHostname(hostname, controller.signal)
      .then((tenant) => {
        if (controller.signal.aborted) return;
        if (tenant && tenant.slug) {
          setResolvedTenant(tenant.slug);
          setState({ status: 'matched', tenant });
        } else {
          setResolvedTenant(null);
          setState({ status: 'unmatched', tenant: null });
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setResolvedTenant(null);
        setState({ status: 'unmatched', tenant: null });
      });

    return () => controller.abort();
  }, []);

  // Memoize context value to avoid unnecessary re-renders of consumers.
  const value = useMemo(() => state, [state]);

  return (
    <HostnameTenantContext.Provider value={value}>
      {children}
    </HostnameTenantContext.Provider>
  );
};

/**
 * Hook to read the hostname-tenant resolution result.
 * @returns {{ status: 'loading'|'matched'|'unmatched', tenant: object|null }}
 */
export const useHostnameTenant = () => useContext(HostnameTenantContext);

export default HostnameTenantContext;
